// Similar to ConversationRowItem.tsx but for timeline display
// Handles:
// - Individual event rendering in timeline format
// - Event type icons and status indicators
// - Timestamp formatting
// - Associated record links
// - Click handling for event details
// - Uses Message.tsx component for message-related events 

import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Avatar,
  Chip,
  IconButton,
  useTheme,
  alpha,
  Collapse,
  Stack,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Fade,
  Card,
  CardContent,
  Divider,
  useMediaQuery,
  Zoom,
  LinearProgress,
} from '@mui/material';
import {
  Message as MessageIcon,
  Note as NoteIcon,
  Forum as ForumIcon,
  Person as PersonIcon,
  Event as EventIcon,
  MoreVert as MoreVertIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Code as CodeIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
  OpenInNew as OpenInNewIcon,
  KeyboardArrowDown as ExpandMoreIcon,
  KeyboardArrowUp as ExpandLessIcon,
  DataObject as DataObjectIcon,
  AccessTime as AccessTimeIcon,
  Source as SourceIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  HourglassEmpty as PendingIcon,
  Visibility as VisibilityIcon,
  Link as LinkIcon,
  AttachFile as AttachFileIcon,
  AccountCircle as AccountCircleIcon,
  Settings as SettingsIcon,
  Update as UpdateIcon,
  Public as PublicIcon,
} from '@mui/icons-material';
import { Event } from 'src/features/timeline/events.slice';
import { dateTimeFormatter } from 'src/libs/dateTimeFormatter';
import Message from '../Channels/Message';
import { motion } from 'framer-motion';
import { useSnackbar } from 'notistack';
import { useAppDispatch } from 'src/store';
import { deleteEvent } from 'src/features/timeline/events.slice';
import ReactJson from 'react-json-view';

interface TimelineEventItemProps {
  event: Event;
  selected: boolean;
  onClick: () => void;
  isLoadingFullData?: boolean;
}

// Animated components
const MotionPaper = motion(Paper);
const MotionBox = motion(Box);

// Enhanced status configurations
const STATUS_CONFIG = {
  active: { icon: <CheckCircleIcon />, color: '#4CAF50', label: 'Active' },
  completed: { icon: <CheckCircleIcon />, color: '#2196F3', label: 'Completed' },
  error: { icon: <ErrorIcon />, color: '#f44336', label: 'Error' },
  pending: { icon: <PendingIcon />, color: '#FF9800', label: 'Pending' },
};

const TimelineEventItem: React.FC<TimelineEventItemProps> = ({
  event,
  selected,
  onClick,
  isLoadingFullData = false,
}) => {
  const theme = useTheme();
  const darkMode = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();
  
  const [expanded, setExpanded] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [payloadDialogOpen, setPayloadDialogOpen] = useState(false);
  const menuOpen = Boolean(anchorEl);

  // Get event icon based on type
  const getEventIcon = () => {
    const iconMap: { [key: string]: JSX.Element } = {
      message_sent: <MessageIcon />,
      message_received: <MessageIcon />,
      message_delivered: <MessageIcon />,
      note_created: <NoteIcon />,
      note_updated: <NoteIcon />,
      conversation_created: <ForumIcon />,
      conversation_status_changed: <ForumIcon />,
      contact_created: <PersonIcon />,
      contact_updated: <PersonIcon />,
      phone_call: <PhoneIcon />,
      email_sent: <EmailIcon />,
      email_received: <EmailIcon />,
      system: <CodeIcon />,
      // Add new event types from the data
      notesCreated: <NoteIcon />,
      notesUpdated: <NoteIcon />,
      tasksCreated: <EventIcon />,
      tasksUpdated: <EventIcon />,
      textMessagesCreated: <MessageIcon />,
      textMessagesUpdated: <MessageIcon />,
      peopleTagsCreated: <PersonIcon />,
      callsCreated: <PhoneIcon />,
      callsUpdated: <PhoneIcon />,
      emEventsOpened: <EmailIcon />,
    };
    return iconMap[event.event_type] || <EventIcon />;
  };

  // Enhanced color scheme based on event type and source
  const getEventColor = () => {
    const colorMap: { [key: string]: string } = {
      twilio: theme.palette.primary.main,
      email: theme.palette.secondary.main,
      system: theme.palette.grey[500],
      api: theme.palette.info.main,
      manual: theme.palette.success.main,
    };
    
    if (event.source && colorMap[event.source]) {
      return colorMap[event.source];
    }
    
    // Color by event type
    if (event.event_type?.includes('note')) return '#FF9800';
    if (event.event_type?.includes('textMessage')) return '#2196F3';
    if (event.event_type?.includes('call')) return '#3F51B5';
    if (event.event_type?.includes('peopleTag')) return '#E91E63';
    if (event.event_type?.includes('task')) return '#9C27B0';
    if (event.event_type?.includes('error')) return theme.palette.error.main;
    if (event.event_type?.includes('success')) return theme.palette.success.main;
    return theme.palette.info.main;
  };

  // Extract useful information from payload
  const getEventDetails = () => {
    const details: { label: string; value: string; isHtml?: boolean }[] = [];
    
    if (event.payload) {
      // For notes events - add subject if exists
      if (event.event_type?.includes('notes')) {
        if (event.payload.subject) {
          details.push({
            label: 'Subject',
            value: event.payload.subject
          });
        }
        // Only show the first available of body or content as Note
        let noteValue = null;
        let isHtml = false;
        if (event.payload.body) {
          noteValue = event.payload.body;
          isHtml = event.payload.isHtml === true;
        } else if (event.payload.content) {
          noteValue = event.payload.content;
          isHtml = event.payload.isHtml === true;
        }
        if (noteValue) {
          details.push({
            label: 'Note',
            value: noteValue,
            isHtml
          });
        }
      }
      
      // For people tags events
      if (event.event_type === 'peopleTagsCreated' && event.payload.data?.tags) {
        details.push({
          label: 'Tags Added',
          value: event.payload.data.tags.join(', ')
        });
      }
      
      // Resource URIs
      if (event.payload.uri) {
        const resourceType = event.payload.uri.split('/').slice(-2, -1)[0];
        const resourceId = event.payload.uri.split('=').pop();
        details.push({
          label: resourceType.charAt(0).toUpperCase() + resourceType.slice(1),
          value: `#${resourceId}`
        });
      }
    }
    
    return details;
  };

  const eventDetails = getEventDetails();

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleCopyPayload = () => {
    navigator.clipboard.writeText(JSON.stringify(event.payload, null, 2));
    enqueueSnackbar('Payload copied to clipboard', { variant: 'success' });
    handleMenuClose();
  };

  const handleDelete = async () => {
    try {
      await dispatch(deleteEvent(event.id)).unwrap();
      enqueueSnackbar('Event deleted successfully', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Failed to delete event', { variant: 'error' });
    }
    handleMenuClose();
  };

  const handleExpandClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setExpanded(!expanded);
  };

  // Render message content if event has associated messages
  const renderMessageContent = () => {
    if (!event.associations?.messages || event.associations.messages.length === 0) {
      return null;
    }

    const message = event.associations.messages[0];
    const messageData = {
      ...message,
      sender: message.sender || {
        id: message.sender_id,
        role: message.sender_role,
        name: message.sender_name,
      },
    };

    return (
      <Box sx={{ mt: 2 }}>
        <Message
          message={messageData}
          sendingEntity={messageData.sender}
          channelId={event.associations?.conversations?.[0]?.channel_id}
          variant="compact"
          parentConversation={event.associations?.conversations?.[0]}
        />
      </Box>
    );
  };

  // Enhanced associated record chips
  const renderAssociations = () => {
    const chips = [];

    if (event.associations?.contacts?.length > 0) {
      event.associations.contacts.forEach(contact => {
        chips.push(
          <motion.div
            key={`contact-${contact.id}`}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Chip
              label={contact.name || contact.email || contact.phone_number}
              size="small"
              icon={<PersonIcon />}
              sx={{
                mr: 0.5,
                mb: 0.5,
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                color: theme.palette.primary.main,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                '& .MuiChip-icon': {
                  color: theme.palette.primary.main,
                },
              }}
            />
          </motion.div>
        );
      });
    }

    if (event.associations?.conversations?.length > 0) {
      event.associations.conversations.forEach(conv => {
        chips.push(
          <motion.div
            key={`conv-${conv.id}`}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Chip
              label={conv.subject || `Conversation #${conv.id.slice(0, 8)}`}
              size="small"
              icon={<ForumIcon />}
              sx={{
                mr: 0.5,
                mb: 0.5,
                bgcolor: alpha(theme.palette.secondary.main, 0.1),
                color: theme.palette.secondary.main,
                border: `1px solid ${alpha(theme.palette.secondary.main, 0.3)}`,
                '& .MuiChip-icon': {
                  color: theme.palette.secondary.main,
                },
              }}
            />
          </motion.div>
        );
      });
    }

    if (event.associations?.notes?.length > 0) {
      event.associations.notes.forEach(note => {
        chips.push(
          <motion.div
            key={`note-${note.id}`}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Chip
              label={note.subject || 'Note'}
              size="small"
              icon={<NoteIcon />}
              sx={{
                mr: 0.5,
                mb: 0.5,
                bgcolor: alpha(theme.palette.warning.main, 0.1),
                color: theme.palette.warning.main,
                border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
                '& .MuiChip-icon': {
                  color: theme.palette.warning.main,
                },
              }}
            />
          </motion.div>
        );
      });
    }

    return chips.length > 0 ? (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', mt: 1 }}>
        {chips}
      </Box>
    ) : null;
  };

  // Enhanced metadata preview
  const renderMetadataPreview = () => {
    if (!event.metadata || Object.keys(event.metadata).length === 0) return null;

    const entries = Object.entries(event.metadata);
    const displayEntries = entries.slice(0, 3);
    const hasMore = entries.length > 3;

    return (
      <Card
        sx={{
          mt: 2,
          bgcolor: alpha(theme.palette.primary.main, 0.05),
          border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          borderRadius: 2,
          backdropFilter: 'blur(10px)',
        }}
      >
        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Typography variant="caption" sx={{ fontWeight: 600, color: 'primary.main' }}>
            <DataObjectIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
            Metadata
          </Typography>
          <Stack spacing={0.5} sx={{ mt: 1 }}>
            {displayEntries.map(([key, value]) => (
              <Typography key={key} variant="caption" sx={{ display: 'flex', alignItems: 'center' }}>
                <Box component="span" sx={{ fontWeight: 500, mr: 1 }}>{key}:</Box>
                <Box component="span" sx={{ color: 'text.secondary' }}>
                  {typeof value === 'object' ? JSON.stringify(value) : value}
                </Box>
              </Typography>
            ))}
            {hasMore && (
              <Typography variant="caption" sx={{ color: 'primary.main', cursor: 'pointer' }} onClick={() => setPayloadDialogOpen(true)}>
                +{entries.length - 3} more...
              </Typography>
            )}
          </Stack>
        </CardContent>
      </Card>
    );
  };

  const statusConfig = STATUS_CONFIG[event.status as keyof typeof STATUS_CONFIG];

  return (
    <>
      <MotionPaper
        elevation={selected ? 8 : 2}
        onClick={onClick}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        whileHover={{ y: -2 }}
        transition={{ duration: 0.2 }}
        sx={{
          p: 2,
          cursor: 'pointer',
          position: 'relative',
          overflow: 'visible',
          bgcolor: selected 
            ? alpha(theme.palette.primary.main, darkMode ? 0.15 : 0.08)
            : darkMode ? theme.palette.background.paper : theme.palette.background.paper,
          border: selected ? `2px solid ${theme.palette.primary.main}` : `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          borderRadius: 3,
          backdropFilter: 'blur(10px)',
          background: darkMode
            ? `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.95)} 0%, ${alpha(theme.palette.background.paper, 0.85)} 100%)`
            : `linear-gradient(135deg, ${alpha('#ffffff', 0.95)} 0%, ${alpha('#ffffff', 0.85)} 100%)`,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            boxShadow: theme.shadows[selected ? 12 : 6],
            border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
          },
        }}
      >
        {/* Loading indicator for full data */}
        {isLoadingFullData && (
          <LinearProgress
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 3,
              borderRadius: '12px 12px 0 0',
            }}
          />
        )}

        {/* Gradient accent line */}
        {!isLoadingFullData && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 3,
              background: `linear-gradient(90deg, ${getEventColor()} 0%, ${alpha(getEventColor(), 0.6)} 100%)`,
              borderRadius: '12px 12px 0 0',
            }}
          />
        )}

        {/* Main content */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
          {/* Enhanced Event Icon */}
          <Zoom in={true}>
            <Avatar
              sx={{
                bgcolor: alpha(getEventColor(), 0.1),
                color: getEventColor(),
                width: 40,
                height: 40,
                mr: 1.5,
                boxShadow: `0 2px 8px ${alpha(getEventColor(), 0.2)}`,
                border: `1px solid ${alpha(getEventColor(), 0.2)}`,
              }}
            >
              {getEventIcon()}
            </Avatar>
          </Zoom>

          {/* Event Content */}
          <Box sx={{ flex: 1 }}>
            {/* Title and Actions Row */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: darkMode ? 'text.primary' : 'text.primary' }}>
                {event.message || event.event_type?.replace(/_/g, ' ').replace(/([A-Z])/g, ' $1').replace(/\b\w/g, l => l.toUpperCase()).trim()}
              </Typography>
              
              {/* Action buttons */}
              <Stack direction="row" spacing={0.5}>
                {/* Expand/Collapse button */}
                {(event.description || event.payload || event.metadata || eventDetails.length > 0) && (
                  <IconButton
                    onClick={handleExpandClick}
                    size="small"
                    sx={{
                      p: 0.5,
                      transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.3s',
                      color: darkMode ? 'text.secondary' : 'text.primary',
                    }}
                  >
                    <ExpandMoreIcon fontSize="small" />
                  </IconButton>
                )}
                
                {/* More options menu */}
                <IconButton onClick={handleMenuOpen} size="small" sx={{ p: 0.5, color: darkMode ? 'text.secondary' : 'text.primary' }}>
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              </Stack>
            </Box>

            {/* Condensed metadata row - all in one line */}
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 0.5 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <AccessTimeIcon sx={{ fontSize: 14 }} />
                {dateTimeFormatter(event.created_at, 'short')}
              </Typography>
              
              {event.source && (
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  • {event.source}
                </Typography>
              )}
              
              {statusConfig && (
                <Typography variant="caption" sx={{ color: statusConfig.color, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  • {statusConfig.label}
                </Typography>
              )}
              
              {/* Show createdBy for created events */}
              {event.event_type?.toLowerCase().includes('created') && event.payload?.createdBy && (
                <Chip
                  icon={<AccountCircleIcon />}
                  label={event.payload.createdBy}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: '0.7rem',
                    bgcolor: alpha(theme.palette.success.main, 0.1),
                    color: theme.palette.success.main,
                    border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
                    '& .MuiChip-icon': {
                      fontSize: 16,
                      color: theme.palette.success.main,
                    },
                  }}
                />
              )}
              
              {/* Show updatedBy for updated events */}
              {event.event_type?.toLowerCase().includes('updated') && event.payload?.updatedBy && (
                <Chip
                  icon={<UpdateIcon />}
                  label={event.payload.updatedBy}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: '0.7rem',
                    bgcolor: alpha(theme.palette.info.main, 0.1),
                    color: theme.palette.info.main,
                    border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
                    '& .MuiChip-icon': {
                      fontSize: 16,
                      color: theme.palette.info.main,
                    },
                  }}
                />
              )}
              
              {/* Show systemName if available */}
              {event.payload?.systemName && (
                <Chip
                  icon={<SettingsIcon />}
                  label={event.payload.systemName}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: '0.7rem',
                    bgcolor: alpha(theme.palette.secondary.main, 0.1),
                    color: theme.palette.secondary.main,
                    border: `1px solid ${alpha(theme.palette.secondary.main, 0.3)}`,
                    '& .MuiChip-icon': {
                      fontSize: 16,
                      color: theme.palette.secondary.main,
                    },
                  }}
                />
              )}
              
              {/* Show external indicator */}
              {event.payload?.isExternal === true && (
                <Chip
                  icon={<PublicIcon />}
                  label="External"
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: '0.7rem',
                    bgcolor: alpha(theme.palette.warning.main, 0.1),
                    color: theme.palette.warning.main,
                    border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
                    '& .MuiChip-icon': {
                      fontSize: 16,
                      color: theme.palette.warning.main,
                    },
                  }}
                />
              )}
            </Stack>

            {/* Show primary content (note body or subject) with proper wrapping */}
            {!expanded && eventDetails.length > 0 && (
              <>
                {/* Show subject as title if available */}
                {eventDetails.find(d => d.label === 'Subject') && (
                  <Typography 
                    variant="subtitle2" 
                    sx={{ 
                      fontWeight: 500,
                      color: darkMode ? 'text.primary' : 'text.primary',
                      mb: 0.5,
                    }}
                  >
                    {eventDetails.find(d => d.label === 'Subject')?.value}
                  </Typography>
                )}
                {/* Show note body if available (only first available, isHtml respected) */}
                {(() => {
                  const noteDetail = eventDetails.find(d => d.label === 'Note');
                  if (!noteDetail) return null;
                  if (noteDetail.isHtml) {
                    return (
                      <Box
                        sx={{
                          '& > div': {
                            color: darkMode ? 'text.primary' : 'text.primary',
                            maxHeight: '7.5em', // ~5 lines
                            overflow: 'hidden',
                            position: 'relative',
                            lineHeight: 1.5,
                            fontSize: '0.875rem',
                            '& p': { 
                              margin: '0 0 0.5em 0',
                              '&:last-child': { marginBottom: 0 }
                            },
                            '& br': { display: 'block', content: '""', marginTop: '0.5em' },
                            '&::after': {
                              content: '""',
                              position: 'absolute',
                              bottom: 0,
                              right: 0,
                              width: '100%',
                              height: '1.5em',
                              background: darkMode 
                                ? `linear-gradient(to bottom, transparent, ${theme.palette.background.paper})`
                                : `linear-gradient(to bottom, transparent, ${theme.palette.background.paper})`,
                              pointerEvents: 'none',
                            },
                          },
                        }}
                      >
                        <div dangerouslySetInnerHTML={{ __html: noteDetail.value || '' }} />
                      </Box>
                    );
                  } else {
                    return (
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: darkMode ? 'text.primary' : 'text.primary',
                          display: '-webkit-box',
                          WebkitLineClamp: 5,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          wordBreak: 'break-word',
                          lineHeight: 1.5,
                          mb: 1,
                        }}
                      >
                        {noteDetail.value}
                      </Typography>
                    );
                  }
                })()}
              </>
            )}

            {/* Show other details inline if not a note or if there are additional details */}
            {!expanded && eventDetails.length > 0 && 
             eventDetails.some(d => d.label !== 'Note' && d.label !== 'Subject') && (
              <Stack spacing={0.5}>
                {eventDetails
                  .filter(d => d.label !== 'Note' && d.label !== 'Subject')
                  .slice(0, 2)
                  .map((detail, idx) => (
                    <Typography key={idx} variant="caption" sx={{ color: 'text.secondary' }}>
                      {detail.label}: {detail.value.length > 50 ? detail.value.substring(0, 50) + '...' : detail.value}
                    </Typography>
                  ))}
                {eventDetails.filter(d => d.label !== 'Note' && d.label !== 'Subject').length > 2 && (
                  <Typography variant="caption" sx={{ color: 'primary.main', cursor: 'pointer' }} onClick={handleExpandClick}>
                    +{eventDetails.filter(d => d.label !== 'Note' && d.label !== 'Subject').length - 2} more details...
                  </Typography>
                )}
              </Stack>
            )}

            {/* Associated Records - inline */}
            {renderAssociations()}

            {/* Collapsible detailed content */}
            <Collapse in={expanded} timeout="auto" unmountOnExit>
              <Box sx={{ mt: 1.5 }}>
                {/* Event Details when expanded - show full content */}
                {eventDetails.length > 0 && (
                  <Box sx={{ mb: 1.5 }}>
                    {eventDetails.map((detail, index) => (
                      <Box key={index} sx={{ mb: 0.5 }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                          {detail.label}:
                        </Typography>
                        {detail.label === 'Note' && detail.isHtml ? (
                          <Box
                            sx={{
                              color: darkMode ? 'text.primary' : 'text.primary',
                              mt: 0.5,
                              '& p': { margin: 0 },
                              '& > div': {
                                lineHeight: 1.5,
                                wordBreak: 'break-word',
                              },
                            }}
                          >
                            <div dangerouslySetInnerHTML={{ __html: detail.value }} />
                          </Box>
                        ) : (
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: darkMode ? 'text.primary' : 'text.primary',
                              whiteSpace: detail.label === 'Note' ? 'pre-wrap' : 'normal',
                              wordBreak: 'break-word',
                            }}
                          >
                            {detail.value}
                          </Typography>
                        )}
                      </Box>
                    ))}
                  </Box>
                )}

                {/* Description */}
                {event.description && (
                  <Typography variant="body2" sx={{ mb: 1.5, color: darkMode ? 'text.secondary' : 'text.secondary' }}>
                    {event.description}
                  </Typography>
                )}

                {/* Message Content (if applicable) */}
                {renderMessageContent()}

                {/* Metadata Preview */}
                {renderMetadataPreview()}
                
                {/* Payload Preview */}
                {event.payload && Object.keys(event.payload).length > 0 && (
                  <Card
                    sx={{
                      mt: 1.5,
                      bgcolor: alpha(theme.palette.info.main, 0.05),
                      border: `1px solid ${alpha(theme.palette.info.main, 0.1)}`,
                      borderRadius: 2,
                      backdropFilter: 'blur(10px)',
                    }}
                  >
                    <CardContent sx={{ py: 1, px: 1.5, '&:last-child': { pb: 1 } }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="caption" sx={{ fontWeight: 600, color: darkMode ? theme.palette.info.light : theme.palette.info.main }}>
                          <DataObjectIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                          Payload
                        </Typography>
                        <Button
                          size="small"
                          startIcon={<VisibilityIcon />}
                          onClick={() => setPayloadDialogOpen(true)}
                          sx={{ 
                            textTransform: 'none',
                            color: darkMode ? theme.palette.info.light : theme.palette.info.main,
                            fontSize: '0.75rem',
                            py: 0.25,
                          }}
                        >
                          View Full
                        </Button>
                      </Stack>
                    </CardContent>
                  </Card>
                )}
              </Box>
            </Collapse>
          </Box>
        </Box>

        {/* Menu */}
        <Menu
          anchorEl={anchorEl}
          open={menuOpen}
          onClose={handleMenuClose}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          PaperProps={{
            elevation: 8,
            sx: {
              minWidth: 200,
              borderRadius: 2,
              mt: 1,
              '& .MuiMenuItem-root': {
                px: 2,
                py: 1,
                '& .MuiListItemIcon-root': {
                  minWidth: 36,
                },
              },
            },
          }}
        >
          <MenuItem onClick={handleCopyPayload} disabled={!event.payload}>
            <ListItemIcon>
              <CopyIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Copy Payload" />
          </MenuItem>
          <MenuItem onClick={() => setPayloadDialogOpen(true)} disabled={!event.payload && !event.metadata}>
            <ListItemIcon>
              <VisibilityIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="View Details" />
          </MenuItem>
          <MenuItem onClick={() => console.log('Edit not implemented yet')}>
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Edit Event" />
          </MenuItem>
          <Divider sx={{ my: 1 }} />
          <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
            <ListItemIcon>
              <DeleteIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText primary="Delete" />
          </MenuItem>
        </Menu>

        {/* Enhanced Payload/Metadata Dialog */}
        <Dialog
          open={payloadDialogOpen}
          onClose={() => setPayloadDialogOpen(false)}
          maxWidth="md"
          fullWidth
          TransitionComponent={Zoom}
          PaperProps={{
            sx: {
              borderRadius: 3,
              maxHeight: '80vh',
            },
          }}
        >
          <DialogTitle sx={{ 
            borderBottom: `1px solid ${theme.palette.divider}`,
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
            color: 'white',
          }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <DataObjectIcon />
              <Typography variant="h6">Event Details</Typography>
            </Stack>
          </DialogTitle>
          <DialogContent sx={{ p: 3 }}>
            {event.payload && (
              <>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                  Payload
                </Typography>
                <Box sx={{ 
                  bgcolor: darkMode ? 'grey.900' : 'grey.50', 
                  p: 2, 
                  borderRadius: 2,
                  border: `1px solid ${theme.palette.divider}`,
                  maxHeight: 400,
                  overflow: 'auto',
                }}>
                  <ReactJson
                    src={event.payload}
                    theme={darkMode ? 'monokai' : 'rjv-default'}
                    collapsed={2}
                    displayDataTypes={false}
                    enableClipboard
                    style={{ backgroundColor: 'transparent' }}
                  />
                </Box>
              </>
            )}
            
            {event.metadata && (
              <>
                <Typography variant="subtitle1" sx={{ mt: 3, mb: 2, fontWeight: 600 }}>
                  Metadata
                </Typography>
                <Box sx={{ 
                  bgcolor: darkMode ? 'grey.900' : 'grey.50', 
                  p: 2, 
                  borderRadius: 2,
                  border: `1px solid ${theme.palette.divider}`,
                  maxHeight: 400,
                  overflow: 'auto',
                }}>
                  <ReactJson
                    src={event.metadata}
                    theme={darkMode ? 'monokai' : 'rjv-default'}
                    collapsed={2}
                    displayDataTypes={false}
                    enableClipboard
                    style={{ backgroundColor: 'transparent' }}
                  />
                </Box>
              </>
            )}
          </DialogContent>
        </Dialog>
      </MotionPaper>
    </>
  );
};

export default TimelineEventItem; 
